import { Component, input, model } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  CREATIVE_ASSISTANT_DOCUMENT_FILENAMES,
  getCreativeAssistantFilenamesIssue,
} from '@shared';
import {
  AssistantBuilderFileUploadComponent,
  AssistantBuilderFileValue,
} from './assistant-builder-file-upload.component';

export type AssistantBuilderTraitCreativeFilesValue =
  AssistantBuilderFileValue[];

export interface AssistantBuilderTraitCreativeFormValues {
  files: AssistantBuilderTraitCreativeFilesValue;
}

export type AssistantBuilderTraitCreativeForm = {
  [K in keyof AssistantBuilderTraitCreativeFormValues]: FormControl<
    AssistantBuilderTraitCreativeFormValues[K]
  >;
};

function creativeConfigurationFilesValidator(
  group: AbstractControl,
): ValidationErrors | null {
  const files = group.get('files')?.value as
    | AssistantBuilderTraitCreativeFilesValue
    | undefined;

  if (!files?.length) {
    return { creativeConfigurationFiles: true };
  }

  const filenames = files.map((file) => file.name);
  return getCreativeAssistantFilenamesIssue(filenames) === null
    ? null
    : { creativeConfigurationFiles: true };
}

interface CreativeConfigurationFileRuleLine {
  translationKey: string;
  params?: Record<string, string>;
}

@Component({
  selector: 'squadbox-assistant-builder-trait-creative-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    AssistantBuilderFileUploadComponent,
    fromSeriousSystem.TextareaComponent,
  ],
  template: ` @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <div class="flex flex-col gap-2">
        <squadbox-assistant-builder-file-upload
          [label]="
            'ASSISTANTS.SAVE_MODAL.FORM.CREATIVE.CONFIGURATION_FILES.LABEL'
              | translate
          "
          [placeholder]="
            'ASSISTANTS.SAVE_MODAL.FORM.CREATIVE.CONFIGURATION_FILES.PLACEHOLDER'
              | translate
          "
          filePickerButtonIcon="plus"
          formControlName="files"
          [fileSupportedExtensions]="documentUploadSupportedExtensions"
          [transactionId]="fileUploadTransactionId()"
        ></squadbox-assistant-builder-file-upload>
        <ul class="typo-caption text-neutral-700 mt-0 space-y-1 list-disc pl-5">
          @for (
            rule of creativeConfigurationFileRules;
            track rule.translationKey
          ) {
            <li
              [innerHTML]="rule.translationKey | translate: rule.params ?? {}"
            ></li>
          }
        </ul>
      </div>
    </form>`,
})
export class AssistantBuilderTraitCreativeFormComponent {
  public readonly fileUploadTransactionId = input.required<string>();

  public readonly creativeConfigurationFileRules: CreativeConfigurationFileRuleLine[] =
    [
      {
        translationKey:
          'ASSISTANTS.SAVE_MODAL.FORM.CREATIVE.CONFIGURATION_FILES.RULES.RULE_1',
        params: {
          schemaFile: CREATIVE_ASSISTANT_DOCUMENT_FILENAMES.schemaFile,
          rulesFile: CREATIVE_ASSISTANT_DOCUMENT_FILENAMES.rulesFile,
        },
      },
      {
        translationKey:
          'ASSISTANTS.SAVE_MODAL.FORM.CREATIVE.CONFIGURATION_FILES.RULES.RULE_2',
        params: {
          templateFileEn: CREATIVE_ASSISTANT_DOCUMENT_FILENAMES.templateFileEn,
          templateFileFr: CREATIVE_ASSISTANT_DOCUMENT_FILENAMES.templateFileFr,
        },
      },
    ];

  public readonly form = model<FormGroup<AssistantBuilderTraitCreativeForm>>(
    new FormGroup<AssistantBuilderTraitCreativeForm>(
      {
        files: new FormControl<AssistantBuilderTraitCreativeFilesValue>(
          {
            value: [],
            disabled: false,
          },
          { nonNullable: true },
        ),
      },
      {
        validators: [creativeConfigurationFilesValidator],
      },
    ),
  );

  public readonly documentUploadSupportedExtensions = ['md', 'yml'];
}
